

import {
  ExpandMore as ExpandMoreIcon,
  AccountTree as GraphIcon,
  ViewList as ListIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { EntitySummary, EventSummary } from '@/lib/types';

import AuthGuard from '@/components/auth-guard';
import { Page } from '@/components/page';
import {
  RelationshipEntityTypeFilter,
  type RelationshipEntityTypeFilterOption,
} from '@/components/relationship-entity-type-filter';
import { RelationshipGraph } from '@/components/relationship-graph';
import { useCharacterAccessLayer } from '@/features/characters';
import { useCodexAccessLayer } from '@/features/codex';
import { useEventAccessLayer } from '@/features/events';
import { useLocationAccessLayer } from '@/features/locations';
import { useOrganizationAccessLayer } from '@/features/organizations';
import {
  isOrganizationHierarchyManagedRelationship,
  ORGANIZATION_HIERARCHY_FEATURE_KEY,
} from '@/features/organizations/utils/organization-hierarchy-feature';
import { useUserProfile } from '@/firebase/hooks/use-user-profile';
import { useAllRelationships } from '@/hooks/use-entities';
import { useRelationshipEntityTypeFilter } from '@/hooks/use-relationship-entity-type-filter';
import { useResponsiveValue } from '@/hooks/use-responsive-value';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { useSubscription } from '@/hooks/useSubscription';

type AnyEntitySummary = (EntitySummary | EventSummary) & { entityType: string };
type RelationshipViewMode = 'list' | 'graph';

const getRelationshipViewMode = (view: unknown): RelationshipViewMode | undefined => {
  if (view === 'graph') return 'graph';
  if (view === 'list') return 'list';
  return undefined;
};

export const Route = createFileRoute('/relationships/')({
  validateSearch: (search: Record<string, unknown>) => ({
    view: getRelationshipViewMode(search.view),
  }),
  component: RelationshipsPage,
});

function EntityList({
  entities,
  entityLabel,
  viewMode,
}: {
  entities: AnyEntitySummary[];
  entityLabel: string;
  viewMode: RelationshipViewMode;
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const resolveResponsive = useResponsiveValue();
  const { t } = useTranslation();

  const filteredEntities = entities.filter(entity =>
    entity.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box>
      <TextField
        fullWidth
        placeholder={t('relationships.search_placeholder', { entityType: entityLabel.toLowerCase() })}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        variant="outlined"
        size="small"
        sx={{ mb: 3 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18 }} />
              </InputAdornment>
            ),
          },
        }}
      />
      <Grid container spacing={2}>
        {filteredEntities.map(entity => (
          <Grid key={entity.id} size={resolveResponsive({ xs: 12, sm: 6, lg: 4, xl: 3 })}>
            <Card
              component={Link}
              to={`/relationships/${entity.entityType}/${entity.id}?view=${viewMode}`}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                textDecoration: 'none',
                transition: 'all 0.2s ease-in-out',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                }
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="text.primary"
                  noWrap
                  sx={{ mb: 1 }}
                >
                  {entity.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: 1.4
                }}>
                  {entity.shortDescription || t('relationships.no_description')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
        {filteredEntities.length === 0 && (
          <Grid size={resolveResponsive({ xs: 12 })}>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
              {t('relationships.no_results', { entityType: entityLabel.toLowerCase(), searchTerm })}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

function RelationshipsContent() {
  const { userData, updateProfile, isLoading: isUserProfileLoading } = useUserProfile();
  const { novel } = useNovelRepository();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { hasFeature } = useSubscription();
  const isOrganizationHierarchyEnabled = hasFeature(ORGANIZATION_HIERARCHY_FEATURE_KEY);
  const [optimisticViewMode, setOptimisticViewMode] = useState<RelationshipViewMode | null>(null);
  const viewMode: RelationshipViewMode = search.view || optimisticViewMode || userData?.relationshipsViewMode || 'list';
  const { characters, isLoading: loadingChars } = useCharacterAccessLayer();
  const { locations, isLoading: loadingLocs } = useLocationAccessLayer();
  const { organizations, isLoading: loadingOrgs } = useOrganizationAccessLayer();
  const { events, isLoading: loadingEvents } = useEventAccessLayer();
  const { entries: codexEntries, isLoading: loadingCodex } = useCodexAccessLayer();
  const { data: relationships, isLoading: loadingRelationships } = useAllRelationships();
  const {
    hiddenEntityTypeIds,
    showAllEntityTypes,
    toggleEntityType,
  } = useRelationshipEntityTypeFilter(novel?.id);

  const { t } = useTranslation();

  const isLoading = isUserProfileLoading || loadingChars || loadingLocs || loadingOrgs || loadingEvents || loadingCodex
    || (viewMode === 'graph' && loadingRelationships);

  const allEntities = {
    characters: (characters || []).map(e => ({ ...e, entityType: 'characters' })),
    locations: (locations || []).map(e => ({ ...e, entityType: 'locations' })),
    organizations: (organizations || []).map(e => ({ ...e, entityType: 'organizations' })),
    events: (events || []).map(e => ({ ...e, entityType: 'events' })),
    codex: (codexEntries || []).map(e => ({ ...e, entityType: 'codex' })),
  };

  const codexCategoryMap = novel?.codexCategories || {};
  const codexCategories = new Set(Object.keys(codexCategoryMap));
  allEntities.codex.forEach((entry) => {
    if (entry.type) codexCategories.add(entry.type);
  });

  const codexSections = Array.from(codexCategories).map((category) => ({
    id: `codex:${category}`,
    label: codexCategoryMap[category] || category,
    data: allEntities.codex.filter((entry) => entry.type === category),
  }));

  const sections = [
    { id: 'characters', label: t('entity_list.characters.title'), data: allEntities.characters },
    { id: 'locations', label: t('entity_list.locations.title'), data: allEntities.locations },
    { id: 'organizations', label: t('entity_list.organizations.title'), data: allEntities.organizations },
    { id: 'events', label: t('entity_list.events.title'), data: allEntities.events },
    ...codexSections,
  ];

  const filterOptions: RelationshipEntityTypeFilterOption[] = sections.map((section) => ({
    id: section.id,
    label: section.label,
  }));
  const visibleSections = sections.filter((section) => !hiddenEntityTypeIds.has(section.id));

  const graphEntities = [
    ...allEntities.characters,
    ...allEntities.locations,
    ...allEntities.organizations,
    ...allEntities.events,
    ...allEntities.codex,
  ];
  const visibleRelationships = useMemo(() => {
    const relationshipList = relationships || [];
    if (isOrganizationHierarchyEnabled) return relationshipList;

    return relationshipList.filter((relationship) => !isOrganizationHierarchyManagedRelationship(relationship));
  }, [isOrganizationHierarchyEnabled, relationships]);

  if (!novel.id) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 8, textAlign: 'center' }}>
        <Typography variant="h2" gutterBottom>{t('relationships.no_novel_title')}</Typography>
        <Typography variant="body1" color="text.secondary">
          {t('relationships.no_novel_desc')}
        </Typography>
      </Box>
    );
  }

  return (
    <Page
      title={t('relationships.title')}
      description={t('relationships.description')}
      layoutMode="scrolling"
      disableBottomPadding={viewMode === 'graph'}
      actions={(
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RelationshipEntityTypeFilter
            hiddenEntityTypeIds={hiddenEntityTypeIds}
            onShowAll={showAllEntityTypes}
            onToggle={toggleEntityType}
            options={filterOptions}
          />
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            size="small"
            onChange={async (_, nextMode: RelationshipViewMode | null) => {
              if (!nextMode) return;
              setOptimisticViewMode(nextMode);
              navigate({
                to: '/relationships',
                search: () => ({ view: nextMode }),
                replace: true,
              });
              if (userData?.relationshipsViewMode !== nextMode) {
                await updateProfile({ relationshipsViewMode: nextMode });
              }
            }}
          >
            <ToggleButton value="list">
              <ListIcon sx={{ fontSize: 18, mr: 1 }} />
              {t('relationships.list_view')}
            </ToggleButton>
            <ToggleButton value="graph">
              <GraphIcon sx={{ fontSize: 18, mr: 1 }} />
              {t('relationships.graph_view')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
    >

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      ) : viewMode === 'list' ? (
        visibleSections.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{ minHeight: 240, display: 'grid', placeItems: 'center', textAlign: 'center', p: 4 }}
          >
            <Typography color="text.secondary">
              {t('relationships.no_entity_types_selected')}
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {visibleSections.map((section) => (
              <Accordion
                key={section.id}
                defaultExpanded={section.id === 'characters'}
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '12px !important',
                  overflow: 'hidden',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 20 }} />}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {section.label} ({section.data.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3, pt: 0 }}>
                  <EntityList
                    entities={section.data}
                    entityLabel={section.label}
                    viewMode={viewMode}
                  />
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )
      ) : (
        <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0 }}>
          <RelationshipGraph
            entities={graphEntities}
            relationships={visibleRelationships}
            codexCategories={novel.codexCategories || {}}
            hiddenEntityTypeIds={hiddenEntityTypeIds}
            fitViewDependency={Array.from(hiddenEntityTypeIds).sort().join('|')}
            novelId={novel.id}
            novelTheme={novel.theme}
            onEntityClick={(entityType, entityId) => navigate({
              to: '/relationships/$entityType/$id',
              params: { entityType, id: entityId },
              search: { view: viewMode },
            })}
          />
        </Box>
      )}
    </Page>
  );
}

export default function RelationshipsPage() {
  return (
    <AuthGuard>
      <RelationshipsContent />
    </AuthGuard>
  );
}
